// src/pages/Payments/PaymentNew.jsx
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import PaymentForm from '../../components/forms/PaymentForm';
import { useStudents } from '../../hooks/useStudents';
import { useIntakes } from '../../hooks/useIntakes';
import { usePayments } from '../../hooks/usePayments';
import { supabase } from '../../supabaseClient';

/**
 * Utility: split a number evenly across N items, rounded to 2 dp.
 * Ensures the sum of parts exactly equals the original total.
 */
function splitEvenly(total, count) {
  const n = Number(total ?? 0);
  const c = Math.max(0, Number(count ?? 0));
  if (c <= 0) return [];
  const base = Math.floor((n / c) * 100) / 100; // floor to 2dp
  const arr = new Array(c).fill(base);
  let assigned = base * c;

  // distribute remainder cents starting from the first
  let remainder = Math.round((n - assigned) * 100);
  for (let i = 0; i < c && remainder > 0; i++) {
    arr[i] = Math.round((arr[i] + 0.01) * 100) / 100;
    remainder -= 1;
  }

  // fix any floating drift
  const sum = Math.round(arr.reduce((s, x) => s + x, 0) * 100) / 100;
  const diff = Math.round((n - sum) * 100) / 100;
  if (Math.abs(diff) >= 0.01) {
    arr[c - 1] = Math.round((arr[c - 1] + diff) * 100) / 100;
  }
  return arr;
}

const PaymentNew = () => {
  const navigate = useNavigate();
  // When creating from student context: /students/:id/payments/new
  const { id: contextStudentId } = useParams();

  const { students = [], loading: studentsLoading } = useStudents?.() ?? { students: [], loading: false };
  const { intakes = [], loading: intakesLoading } = useIntakes?.() ?? { intakes: [], loading: false };

  const {
    addPayment,
    addPaymentEnrollments,
    addOtherChargesBulk,
  } = usePayments();

  const [studentEnrollments, setStudentEnrollments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [enrollmentsReady, setEnrollmentsReady] = useState(!contextStudentId);

  const didInitialLoad = useRef(false);

  const handleCancel = () => {
    if (contextStudentId) {
      navigate(`/students/${contextStudentId}?tab=payments`, { replace: true });
    } else {
      navigate('/payments', { replace: true });
    }
  };
  /**
   * Fetch enrollment_ids that already have PAID allocations for a student
   * We consider payments with status 'Paid' (case-insensitive) and not soft-deleted.
   */
  const fetchPaidEnrollmentIds = useCallback(async (studentId) => {
    const { data, error } = await supabase
      .from('payment_enrollments')
      .select('enrollment_id, payments!inner(id, status, student_id, is_deleted)')
      .eq('payments.student_id', studentId)
      .eq('payments.is_deleted', false)
      .in('payments.status', ['Paid', 'paid', 'PAID']);

    if (error) throw error;
    return (data || []).map((r) => String(r.enrollment_id));
  }, []);

  /**
   * Load enrollments for a student and exclude those already paid.
   */
  const loadEnrollmentsForStudent = useCallback(
    async (studentId) => {
      try {
        setFormError(null);
        if (!studentId) {
          setStudentEnrollments([]);
          return;
        }

        // 1) All enrollments for this student
        const { data: enrollments, error: e1 } = await supabase
          .from('enrollments')
          .select(`
            id,
            student_id,
            intake_id,
            subject_id,
            subjects:subjects ( id, name ),
            intakes:intakes ( id, name )
          `)
          .eq('student_id', studentId)
          .order('created_at', { ascending: true });

        if (e1) throw e1;

        // 2) Exclude enrollments that already have PAID allocations
        const paidIds = await fetchPaidEnrollmentIds(studentId);
        const unpaid = (enrollments || []).filter((e) => !paidIds.includes(String(e.id)));

        setStudentEnrollments(unpaid);
      } catch (err) {
        console.error(err);
        setFormError(err?.message || 'Failed to load enrollments for student.');
        setStudentEnrollments([]);
      }
    },
    [fetchPaidEnrollmentIds]
  );

  // Initial load in Student Context
  useEffect(() => {
    if (!contextStudentId || didInitialLoad.current) return;
    didInitialLoad.current = true;
    setEnrollmentsReady(false);
    loadEnrollmentsForStudent(contextStudentId).finally(() => setEnrollmentsReady(true));
  }, [contextStudentId, loadEnrollmentsForStudent]);

  const prettyError = (eOrMsg, code) => {
    const msg = typeof eOrMsg === 'string' ? eOrMsg : (eOrMsg?.message || String(eOrMsg));
    if (code === '23505' || /duplicate key|invoice.*exists/i.test(msg)) {
      return 'Invoice number already exists.';
    }
    if (/permission denied/i.test(msg)) return 'You do not have permission to perform this action.';
    return msg;
  };

  /**
   * Submit flow (with reconciliation):
   * 1) Create payment (top Amount is the invoice total)
   * 2) Reconcile:
   *    - otherChargesTotal = Σ otherCharges.amount
   *    - enrollmentBudget = topAmount - otherChargesTotal
   *    - if no enrollments: require topAmount ≈ otherChargesTotal
   *    - if enrollments exist: split ONLY enrollmentBudget across them
   * 3) Link enrollments (if any) with even allocation from enrollmentBudget
   * 4) Insert Other Charges (if any)
   * 5) Navigate back
   * If any step after (1) fails, soft-delete the created payment (rollback).
   */
  const handleSubmit = async (paymentData) => {
    setLoading(true);
    setFormError(null);
    let created = null;

    try {
      const payload = {
        student_id: paymentData.student_id,
        payment_type: paymentData.payment_type, // 'Individual' | 'Full'
        payment_method: paymentData.payment_method,
        amount: Number(paymentData.amount || 0),
        payment_date: paymentData.payment_date,
        status: paymentData.status,
        notes: paymentData.notes || null,
        invoice_number: paymentData.invoice_number,
        is_deleted: false,
      };

      // 1) Create payment
      const result = await addPayment(payload);
      if (!result?.ok) {
        throw new Error(prettyError(result?.error || 'Failed to create payment', result?.code));
      }
      created = result.data;

      // Determine selected enrollment ids to link
      let enrollmentIds = [];
      if (paymentData.payment_type === 'Full') {
        // All enrollments for the selected intake
        const intakeId = paymentData.intake_id;
        enrollmentIds = studentEnrollments
          .filter((e) => String(e.intake_id) === String(intakeId))
          .map((e) => String(e.id));
      } else if (paymentData.payment_type === 'Individual') {
        enrollmentIds = (paymentData.enrollment_ids || []).map(String);
      }

      // 2) Reconcile amounts
      const topAmount = Number(paymentData.amount || 0);
      const otherChargesTotal = (paymentData.otherCharges || []).reduce(
        (s, r) => s + Number(r.amount || 0),
        0
      );
      const enrollmentBudget = Math.round((topAmount - otherChargesTotal) * 100) / 100;

      // Other charges cannot exceed the total
      if (enrollmentBudget < 0) {
        throw new Error('Other Charges exceed the payment Amount. Increase Amount or reduce Other Charges.');
      }

      // If no enrollments selected, Amount must equal Other Charges (within tolerance)
      if (enrollmentIds.length === 0 && Math.abs(enrollmentBudget) > 0.01) {
        throw new Error('Amount must equal the sum of Other Charges when no enrollments are selected.');
      }

      // 3) Link enrollments with even allocation using ONLY the remaining budget
      if (enrollmentIds.length > 0) {
        if (enrollmentBudget <= 0) {
          throw new Error('No budget left for enrollments. Increase Amount or remove some Other Charges.');
        }
        const amounts = splitEvenly(enrollmentBudget, enrollmentIds.length);
        const rows = enrollmentIds.map((eid, idx) => ({
          payment_id: created.id,
          enrollment_id: eid,
          amount_applied: amounts[idx],
        }));
        await addPaymentEnrollments(rows);
      }

      // 4) Insert Other Charges (if any)
      if (Array.isArray(paymentData.otherCharges) && paymentData.otherCharges.length > 0) {
        await addOtherChargesBulk({
          paymentId: created.id,
          rows: paymentData.otherCharges.map((r) => ({
            category: r.category,
            description: r.description,
            amount: Number(r.amount || 0),
          })),
        });
      }

      // 5) Navigate
      if (contextStudentId) {
        navigate(`/students/${contextStudentId}?tab=payments`, { replace: true });
      } else {
        navigate('/payments', { replace: true });
      }
    } catch (err) {
      console.error(err);
      // Attempt rollback if payment row was created
      try {
        if (created?.id) {
          await supabase.from('payments').update({ is_deleted: true }).eq('id', created.id);
        }
      } catch {
        // ignore rollback failure
      }
      setFormError(prettyError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="space-y-6">

        <h1 className="text-xl font-bold text-gray-900">Add Payment</h1>

        <Card>
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              {contextStudentId
                ? 'You are creating a payment for this specific student. The student field is locked.'
                : 'Create a payment. You can select enrollments and/or add Other Charges.'}
            </p>
          </div>

          {formError && (
            <div className="mb-4 p-3 rounded bg-red-100 text-red-700">
              {formError}
            </div>
          )}

          {/* Wait for enrollments in student context, otherwise render immediately */}
          {contextStudentId && !enrollmentsReady ? (
            <div className="py-10 text-center text-gray-600">Loading enrollments…</div>
          ) : (
            <PaymentForm
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              students={students}
              intakes={intakes}
              studentEnrollments={studentEnrollments}
              isEdit={false}
              loading={loading}
              error={null}
              onStudentChange={contextStudentId ? undefined : loadEnrollmentsForStudent}
              contextStudentId={contextStudentId}
            />
          )}
        </Card>
      </div>
    </div>
  );
};

export default PaymentNew;
